// Icon service for fetching exchange and cryptocurrency icons

// Exchange icon mapping - using publicly available icons
const EXCHANGE_ICONS = {
  'Binance': 'https://assets.coingecko.com/markets/images/52/small/binance.jpg',
  'Coinbase': 'https://assets.coingecko.com/markets/images/23/small/Coinbase_Coin_Primary.png',
  'Kraken': 'https://assets.coingecko.com/markets/images/29/small/kraken.jpg',
  'Bitfinex': 'https://assets.coingecko.com/markets/images/4/small/bitfinex.png',
  'Huobi': 'https://assets.coingecko.com/markets/images/102/small/huobi_logo.jpg',
  'Okx': 'https://assets.coingecko.com/markets/images/96/small/WeChat_Image_20220117220452.png',
  'Bybit': 'https://assets.coingecko.com/markets/images/698/small/bybit_spot.png',
  'KuCoin': 'https://assets.coingecko.com/markets/images/61/small/kucoin.jpg',
  'Gate': 'https://assets.coingecko.com/markets/images/60/small/gate.png',
  'Bitget': 'https://play-lh.googleusercontent.com/lytzOd89lWk8yszaRimABF08mE24fgr8gifm4htSlh4_FPtSTcXePR1xLcA8n-9oj4pG=w480-h960-rw',
  'Upbit': 'https://play-lh.googleusercontent.com/JfJIcttruxG2ehGJnqIbjtr5NwBux2Gvl3ILh8hP4wJkgVNy4gsQ0zmIdJJl1blkDuk=w480-h960-rw',
  'Bithumb': 'https://play-lh.googleusercontent.com/_gGIl1OGnFwwfFtTej3wtt-J-2oyz-XkQAyhVWho7mJffyZV_J4DYhXhyyYxB7oMEx-q=s96-rw',
  'Korbit': 'https://assets.coingecko.com/markets/images/75/small/korbit.png',
  'Coinone': 'https://s2.coinmarketcap.com/static/img/exchanges/64x64/174.png',
  'Hyperliquid': 'https://s2.coinmarketcap.com/static/img/exchanges/64x64/8112.png',
  'Default': ''
};

// Cache for fetched icons
const iconCache = new Map();

/**
 * Get exchange icon URL
 * @param {string} exchangeName - Name of the exchange
 * @returns {string} Icon URL
 */
export const getExchangeIcon = (exchangeName) => {
  if (!exchangeName) return EXCHANGE_ICONS['Default'];
  
  // Normalize the exchange name
  const normalizedName = exchangeName.trim();
  
  // Check if exact match exists (case sensitive)
  if (EXCHANGE_ICONS[normalizedName]) {
    return EXCHANGE_ICONS[normalizedName];
  }
  
  // Check for case-insensitive exact match
  for (const [key, value] of Object.entries(EXCHANGE_ICONS)) {
    if (key.toLowerCase() === normalizedName.toLowerCase()) {
      return value;
    }
  }
  
  // Check for partial match (case insensitive)
  const lowerExchangeName = normalizedName.toLowerCase();
  for (const [key, value] of Object.entries(EXCHANGE_ICONS)) {
    if (key.toLowerCase().includes(lowerExchangeName) || lowerExchangeName.includes(key.toLowerCase())) {
      return value;
    }
  }
  
  return EXCHANGE_ICONS['Default'];
};

/**
 * Fetch cryptocurrency icon from CoinGecko API
 * @param {string} symbol - Cryptocurrency symbol (e.g., 'BTC', 'ETH')
 * @returns {Promise<string>} Icon URL
 */
export const getCryptoIcon = async (symbol) => {
  if (!symbol) return null;

  const normalizedSymbol = symbol.toLowerCase();

  // Check cache first
  if (iconCache.has(normalizedSymbol)) {
    return iconCache.get(normalizedSymbol);
  }

  try {
    // Use CoinGecko API to search for the cryptocurrency
    const response = await fetch(`https://api.coingecko.com/api/v3/search?query=${symbol}`);
    const data = await response.json();

    if (data.coins && data.coins.length > 0) {
      // Find exact symbol match first
      let coin = data.coins.find(c => c.symbol.toLowerCase() === normalizedSymbol);

      // If no exact match, use the first result
      if (!coin) {
        coin = data.coins[0];
      }

      const iconUrl = coin.large || coin.small || coin.thumb;

      // Cache the result
      iconCache.set(normalizedSymbol, iconUrl);

      return iconUrl;
    }
  } catch (error) {
    console.warn(`Failed to fetch icon for ${symbol}:`, error);
  }

  // Fallback to a default crypto icon if API fails
  const fallbackIcon = 'https://assets.coingecko.com/coins/images/1/small/bitcoin.png';
  iconCache.set(normalizedSymbol, fallbackIcon);
  return fallbackIcon;
};

/**
 * Preload icons for multiple symbols
 * @param {string[]} symbols - Array of cryptocurrency symbols
 * @returns {Promise<Object>} Object mapping symbols to icon URLs
 */
export const preloadCryptoIcons = async (symbols) => {
  const iconPromises = symbols.map(async (symbol) => {
    const icon = await getCryptoIcon(symbol);
    return { symbol, icon };
  });
  
  const results = await Promise.all(iconPromises);
  const iconMap = {};
  
  results.forEach(({ symbol, icon }) => {
    iconMap[symbol] = icon;
  });
  
  return iconMap;
};

/**
 * Get a fallback icon for unknown symbols
 * @returns {string} Fallback icon URL
 */
export const getFallbackCryptoIcon = () => {
  return 'https://assets.coingecko.com/coins/images/1/small/bitcoin.png';
};

/**
 * Clear the icon cache
 */
export const clearIconCache = () => {
  iconCache.clear();
};